import { useEffect, useMemo } from 'react';
import { useGetMetaDatas } from '../../../Lib/Hooks/MetadataService';

type UseMerchHierarchyOptionsParams = {
  currentDivision?: string | null;
  currentDepartment?: string | null;
  currentCategory?: string | null;
  currentClass?: string | null;
  setDepartmentValue?: (value: string) => void;
  setCategoryValue?: (value: string) => void;
  setClassValue?: (value: string) => void;
};

export const useMerchHierarchyOptions = ({
  currentDivision,
  currentDepartment,
  currentCategory,
  currentClass,
  setDepartmentValue,
  setCategoryValue,
  setClassValue,
}: UseMerchHierarchyOptionsParams) => {
  const { data: metadatas } = useGetMetaDatas(['ns_master_merchHierarchy']);

  const divisions = useMemo(
    () => metadatas?.ns_master_merchHierarchy?.divisions ?? null,
    [metadatas],
  );

  const divisionsOptions = useMemo(() => {
    if (!divisions) return [];
    return Object.keys(divisions).map((division) => ({
      label: division,
      value: division,
    }));
  }, [divisions]);

  const categoriesOptions = useMemo(() => {
    if (!divisions || !currentDivision || !currentDepartment) return [];
    const department = divisions?.[currentDivision]?.[currentDepartment];
    if (!department) return [];
    return Object.keys(department).map((category) => ({
      label: category,
      value: category,
    }));
  }, [divisions, currentDivision, currentDepartment]);

  const classOptions = useMemo(() => {
    if (
      !divisions ||
      !currentDivision ||
      !currentCategory ||
      !currentDepartment
    )
      return [];

    const category =
      divisions?.[currentDivision]?.[currentDepartment]?.[currentCategory];
    if (!category) return [];

    return Object.keys(category || {}).map((className) => ({
      label: className,
      value: className,
    }));
  }, [divisions, currentDivision, currentDepartment, currentCategory]);

  const departmentOptions = useMemo(() => {
    if (!divisions || !currentDivision) {
      return [];
    }
    const departments = divisions?.[currentDivision];
    if (!departments) return [];
    return Object.keys(departments).map((department) => ({
      label: department,
      value: department,
    }));
  }, [divisions, currentDivision]);

  useEffect(() => {
    if (
      setDepartmentValue &&
      currentDivision &&
      departmentOptions.length > 0 &&
      !departmentOptions.some((opt) => opt.value === currentDepartment)
    ) {
      setDepartmentValue('');
    }
  }, [currentDivision, departmentOptions, currentDepartment]);

  useEffect(() => {
    if (
      setCategoryValue &&
      currentDepartment &&
      categoriesOptions.length > 0 &&
      !categoriesOptions.some((opt) => opt.value === currentCategory)
    ) {
      setCategoryValue('');
    }
  }, [currentDepartment, categoriesOptions, currentCategory]);

  useEffect(() => {
    if (
      setClassValue &&
      currentCategory &&
      classOptions.length > 0 &&
      !classOptions.some((opt) => opt.value === currentClass)
    ) {
      setClassValue('');
    }
  }, [currentCategory, classOptions, currentClass]);

  return {
    divisionsOptions,
    categoriesOptions,
    classOptions,
    departmentOptions,
  };
};
